import { useState } from 'react';
import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useCalendarQuery } from '@/api/endpoints';
import { Button } from '@/design/Button';
import { EmptyState, ErrorState } from '@/design/EmptyState';
import { distinctVehicleNames } from '@/features/fleet/labels';
import { CalendarYear } from './CalendarYear';
import { SelectInput } from '@/design/Field';
import { Panel } from '@/design/Panel';

/**
 * The calendar.
 *
 * One row per vehicle, one column per day, bars for what is booked. The
 * question it answers is the one asked forty times a day — "is anything free
 * next Friday?" — so the empty space is the information, and the bars are just
 * what fills it in.
 *
 * Bars are positioned as percentages of the window, so the same data draws at
 * any width without a second request.
 */
const DAY_MS = 86_400_000;

/**
 * What a bar can look like, and what each look means.
 *
 * QA asked "how is Anita Rai on trip?" and, in the same breath, to check the
 * symbols. Both come from the same gap: the grid drew four different kinds of
 * thing in two styles, and the key below named two of them.
 *
 * - A **returned or closed** booking was drawn identically to a confirmed one,
 *   so a trip that finished last Tuesday looked like one still to come.
 * - A booking still marked on-trip past its return date looked exactly like a
 *   healthy one. It is neither a mistake nor normal — it is the car that has
 *   not come back, which is the single most urgent row on the screen.
 * - Blocks — servicing, repairs — were drawn and never explained at all.
 */
type BarLook = 'confirmed' | 'on_trip' | 'overdue' | 'finished' | 'block';

const BAR_STYLES: Readonly<Record<BarLook, string>> = {
  confirmed: 'border border-signal/40 bg-signal-wash text-signal',
  on_trip: 'bg-signal text-ink-inverse',
  overdue: 'bg-alert text-ink-inverse',
  finished: 'border border-rule-strong bg-paper text-ink-muted',
  block: 'border border-dashed border-ink-faint bg-paper text-ink-muted',
};

const BAR_LEGEND: readonly { readonly look: BarLook; readonly label: string }[] = [
  { look: 'confirmed', label: 'Booked' },
  { look: 'on_trip', label: 'Out now' },
  { look: 'overdue', label: 'Overdue back' },
  { look: 'finished', label: 'Finished' },
  { look: 'block', label: 'Servicing or blocked' },
];

/**
 * How far the calendar looks, and how tightly it draws.
 *
 * The screen had one toggle: a fortnight or a month. A guest flying in from
 * abroad books months out, so "can we take a booking in December?" was a
 * question this screen could not be asked in November — and that is the
 * question the owner opens it for.
 *
 * Column width is part of the range rather than a fixed number, because the
 * two cannot be chosen separately. A quarter at the fortnight's 44px is a
 * 4,000px page nobody scrolls; a week at the quarter's 12px is a picture of a
 * week rather than a week. So each range carries the width that fits it, and
 * says what it can still afford to label: at 12px a date does not fit, so the
 * month bands above carry the labelling and the columns carry only the bars.
 */
interface Range {
  readonly value: string;
  readonly label: string;
  readonly days: number;
  /** Tailwind-free, because it is arithmetic in a `calc`. */
  readonly column: string;
  readonly showWeekday: boolean;
  readonly showDate: boolean;
  /**
   * Laid out by date instead of by vehicle.
   *
   * A row per vehicle times 365 columns is either a page eleven metres wide
   * or columns a pixel across, so the year drops the row and packs bookings
   * into lanes. Only the year does this; everything shorter keeps the grid.
   */
  readonly asTimeline?: boolean;
}

const RANGES: readonly Range[] = [
  { value: 'week', label: 'Week', days: 7, column: '4.5rem', showWeekday: true, showDate: true },
  {
    value: 'fortnight',
    label: 'Two weeks',
    days: 14,
    column: '2.75rem',
    showWeekday: true,
    showDate: true,
  },
  { value: 'month', label: 'Month', days: 31, column: '2rem', showWeekday: false, showDate: true },
  {
    value: 'quarter',
    label: 'Three months',
    days: 92,
    column: '0.75rem',
    showWeekday: false,
    showDate: false,
  },
  // A year cannot be a day grid at all — see CalendarYear. `column` is unused
  // here and `days` is only what gets asked of the API.
  {
    value: 'year',
    label: 'Year',
    days: 365,
    column: '0',
    showWeekday: false,
    showDate: false,
    asTimeline: true,
  },
];

export function CalendarPage(): ReactElement {
  const [offset, setOffset] = useState(0);
  const [rangeValue, setRangeValue] = useState('fortnight');

  const range = RANGES.find((option) => option.value === rangeValue) ?? RANGES[1]!;
  const days = range.days;

  const from = new Date();
  from.setHours(0, 0, 0, 0);
  from.setDate(from.getDate() + offset);
  // A year that begins on the 14th has a stub month at each end and lines up
  // with nothing. Snapped to the 1st, the month ruler is the actual months.
  if (range.asTimeline) from.setDate(1);
  const fromIso = `${String(from.getFullYear())}-${String(from.getMonth() + 1).padStart(2, '0')}-${String(from.getDate()).padStart(2, '0')}`;

  const { data, isLoading, isError, refetch } = useCalendarQuery({ from: fromIso, days });

  const windowStart = from.getTime();
  const windowEnd = windowStart + days * DAY_MS;

  /** Midnight today, for comparing dates rather than instants. */
  const todayAtMidnight = (() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now.getTime();
  })();

  const columns = Array.from({ length: days }, (_, index) => {
    const date = new Date(windowStart + index * DAY_MS);
    return {
      date,
      label: date.toLocaleDateString('en-GB', { day: '2-digit' }),
      // `short` — Mon, Tue — not `narrow`, which gives single letters where
      // Saturday and Sunday are both "S" and Tuesday and Thursday both "T".
      weekday: date.toLocaleDateString('en-GB', { weekday: 'short' }),
      month: date.toLocaleDateString('en-GB', { month: 'short' }),
      isWeekend: date.getDay() === 6 || date.getDay() === 0,
      // Compared against the real date, not against being first in the window.
      // It used to be `index === 0 && offset === 0`, which is the same thing
      // only as long as the window starts today — true now, and silently wrong
      // the first time anything changes where it starts.
      isToday: date.getTime() === todayAtMidnight,
    };
  });

  /**
   * The month bands above the days.
   *
   * A row of bare numbers — 21, 22, 23 — does not say which month, and a
   * fortnight regularly spans two. Consecutive days of the same month are
   * collapsed into one band spanning them.
   */
  const months = columns.reduce<{ label: string; span: number }[]>((bands, column) => {
    const last = bands[bands.length - 1];
    const label = column.date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    if (last !== undefined && last.label === label) last.span += 1;
    else bands.push({ label, span: 1 });
    return bands;
  }, []);

  /**
   * The same window, as a list of days — for phones.
   *
   * Derived from the bars the grid already has rather than fetched separately:
   * one request feeds both views, so the two can never disagree about what is
   * booked.
   *
   * Days with nothing on them are left out. On a grid, empty space is the
   * answer to "what is free"; in a list, fourteen rows saying "nothing" is
   * just scrolling.
   */
  const agenda = (() => {
    const plates = distinctVehicleNames(data?.vehicles ?? []);
    const time = (iso: string): string =>
      new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    const dayKey = (iso: string): string => new Date(iso).toDateString();

    return columns
      .map((column) => {
        const key = column.date.toDateString();
        const bars = (data?.bars ?? []).filter((bar) => bar.kind !== 'block');

        const out = bars
          .filter((bar) => dayKey(bar.start_at) === key)
          .map((bar) => ({
            id: bar.id,
            at: time(bar.start_at),
            title: bar.title,
            plate: plates.get(bar.vehicle) ?? '',
          }))
          .sort((a, b) => a.at.localeCompare(b.at));

        const back = bars
          .filter((bar) => dayKey(bar.end_at) === key)
          .map((bar) => ({
            id: bar.id,
            at: time(bar.end_at),
            title: bar.title,
            plate: plates.get(bar.vehicle) ?? '',
          }))
          .sort((a, b) => a.at.localeCompare(b.at));

        return {
          key,
          heading: column.date.toLocaleDateString('en-GB', {
            weekday: 'long',
            day: 'numeric',
            month: 'short',
          }),
          isToday: column.isToday,
          out,
          back,
        };
      })
      .filter((day) => day.out.length > 0 || day.back.length > 0);
  })();

  /** Which of the five looks this bar gets. See `BarLook`. */
  function appearanceOf(bar: { kind: string; status: string; end_at: string }): BarLook {
    if (bar.kind === 'block') return 'block';
    if (bar.status === 'returned' || bar.status === 'closed') return 'finished';
    if (bar.status === 'on_trip') {
      return new Date(bar.end_at).getTime() < Date.now() ? 'overdue' : 'on_trip';
    }
    return 'confirmed';
  }

  /**
   * Where a bar sits, snapped to whole days.
   *
   * This used to place bars on the exact clock time, so a car going out at
   * 14:00 drew a bar starting halfway across Tuesday's column — which reads as
   * "free on Tuesday morning" when the honest answer is that the day is gone.
   * A day-per-column calendar answers "which days is this car out"; it cannot
   * answer anything finer, and pretending otherwise loses bookings.
   *
   * So the start rounds down to the day it falls in and the end rounds up to
   * the end of its day: a bar fills every column it touches, edge to edge.
   */
  function place(startIso: string, endIso: string): { left: string; width: string } {
    const dayFloor = (time: number): number => {
      const date = new Date(time);
      date.setHours(0, 0, 0, 0);
      return date.getTime();
    };

    const start = Math.max(dayFloor(new Date(startIso).getTime()), windowStart);
    const end = Math.min(dayFloor(new Date(endIso).getTime()) + DAY_MS, windowEnd);
    const span = windowEnd - windowStart;
    const left = ((start - windowStart) / span) * 100;
    // One full column is the floor, not 1.2% — a same-day booking should look
    // like the day it takes up rather than a sliver.
    const width = Math.max(((end - start) / span) * 100, (DAY_MS / span) * 100);
    return { left: `${String(left)}%`, width: `${String(width)}%` };
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-display font-semibold">Calendar</h1>
        <div className="flex flex-wrap items-center gap-2">
          {/* A page at a time, whatever a page currently is. Stepping a
              week through a three-month view would take twelve presses to
              move the screen once. */}
          <Button onClick={() => setOffset((value) => value - days)} aria-label="Earlier">
            ‹ Back
          </Button>
          <Button onClick={() => setOffset(0)} disabled={offset === 0}>
            Today
          </Button>
          <Button onClick={() => setOffset((value) => value + days)} aria-label="Later">
            Forward ›
          </Button>

          {/* No label above it. A labelled field beside three bare buttons
              sits a line lower than all of them and pulls the row out of
              true; the options say what they are. */}
          <div className="w-40">
            <SelectInput
              id="calendar-range"
              describedBy={undefined}
              ariaLabel="How much of the calendar to show"
              size="base"
              value={rangeValue}
              onChange={setRangeValue}
              options={RANGES.map((option) => ({ value: option.value, label: option.label }))}
            />
          </div>
        </div>
      </div>

      <Panel
        title={range.asTimeline ? 'The year, day by day' : 'The fleet, day by day'}
        note={`${months[0]?.label ?? ''}${
          months.length > 1 ? ` – ${months[months.length - 1]?.label ?? ''}` : ''
        } · confirmed and on-trip bookings hold a vehicle.`}
        current
      >
        {isError ? (
          <ErrorState
            headline="The calendar did not load"
            detail="The server did not answer. Check the connection and load it again."
            onRetry={() => void refetch()}
            retryLabel="Load again"
          />
        ) : isLoading ? (
          <div className="h-64" aria-busy="true" />
        ) : (data?.vehicles.length ?? 0) === 0 ? (
          <EmptyState
            headline="No vehicles to show"
            detail="Add vehicles and the calendar fills in: confirmed and on-trip bookings block their vehicle, with the turnaround gap after each one."
          />
        ) : (
          <>
          {/* --- Phones: the day, as a list ------------------------------
              A fortnight of columns cannot be read on a 390px screen, and
              pinching at a counter with a customer waiting is not a plan. The
              same data, asked the way somebody standing up asks it: what goes
              out today, what comes back. Only days with something on them. */}
          <div className="md:hidden">
            {agenda.length === 0 ? (
              <EmptyState
                headline="Nothing booked in this window"
                detail="Every vehicle is free for these dates. Move forward a week to look further ahead."
              />
            ) : (
              <ul className="divide-y divide-rule">
                {agenda.map((day) => (
                  <li key={day.key} className="px-3 py-3">
                    <p className="flex items-baseline justify-between gap-2">
                      <span className="font-semibold">{day.heading}</span>
                      {day.isToday && (
                        <span className="text-micro font-medium uppercase text-signal">Today</span>
                      )}
                    </p>

                    <div className="mt-2 flex flex-col gap-2">
                      {day.out.map((entry) => (
                        <Link
                          key={`out-${entry.id}`}
                          to={`/bookings/${entry.id}`}
                          className="flex items-center gap-2 rounded-sm border-l-[3px] border-l-signal bg-signal-wash px-2.5 py-2"
                        >
                          <span className="tnum text-small font-medium">{entry.at}</span>
                          <span className="text-micro font-medium uppercase text-signal">Out</span>
                          <span className="min-w-0 flex-1 truncate">{entry.title}</span>
                          <span className="identifier shrink-0 text-micro text-ink-muted">
                            {entry.plate}
                          </span>
                        </Link>
                      ))}

                      {day.back.map((entry) => (
                        <Link
                          key={`back-${entry.id}`}
                          to={`/bookings/${entry.id}`}
                          className="flex items-center gap-2 rounded-sm border-l-[3px] border-l-rule-strong bg-paper px-2.5 py-2"
                        >
                          <span className="tnum text-small font-medium">{entry.at}</span>
                          <span className="text-micro font-medium uppercase text-ink-muted">
                            Back
                          </span>
                          <span className="min-w-0 flex-1 truncate">{entry.title}</span>
                          <span className="identifier shrink-0 text-micro text-ink-muted">
                            {entry.plate}
                          </span>
                        </Link>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* --- Everything wider: the grid ------------------------------
              Scrolls in both directions inside itself rather than moving the
              page, which is what lets the vehicle column and the dates stay
              put while the other axis moves. Scroll right on a wide fleet and
              the plate is still there; scroll down and so is the date. */}
          {range.asTimeline ? (
            <CalendarYear
              bars={data?.bars ?? []}
              vehicles={data?.vehicles ?? []}
              windowStart={windowStart}
              days={days}
              // Straight into the week the day belongs to. The year answers
              // "is March busy"; the grid answers "which car", and this is
              // the shortest path between the two questions.
              onPickDay={(day) => {
                setRangeValue('week');
                setOffset(Math.round((day.getTime() - todayAtMidnight) / DAY_MS));
              }}
            />
          ) : (
          <div className="hidden max-h-[70vh] overflow-auto md:block">
            {/*
              A floor under each day rather than a fixed minimum for the whole
              grid. Thirty days squeezed into one screen width gives columns
              24px wide, which is a picture of a month rather than something
              anybody can read — so past a point the grid gets wider and
              scrolls, which is what the sticky plate column is for.
            */}
            <div style={{ minWidth: `calc(10rem + ${String(days)} * ${range.column})` }}>
              {/* Month band, then day headings.
                  Today is a filled marker, not a hairline border. It was a 3px
                  left edge while Saturday had a filled background — so the eye
                  read the shaded weekend as the highlighted column, and people
                  reported the calendar's "today" as being days out. Whatever
                  marks today has to be louder than anything that does not. */}
              <div className="sticky top-0 z-20 border-b border-rule-strong bg-panel">
                <div className="flex border-b border-rule">
                  <div className="sticky left-0 z-30 w-40 shrink-0 border-r border-rule bg-panel" />
                  <div className="flex flex-1">
                    {months.map((band) => (
                      <div
                        key={band.label}
                        style={{ flexGrow: band.span, flexBasis: 0 }}
                        className="border-r border-rule px-2 py-1 text-micro font-semibold uppercase tracking-wide text-ink-muted last:border-r-0"
                      >
                        {band.label}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex">
                  <div className="sticky left-0 z-30 w-40 shrink-0 border-r border-rule bg-panel px-3 py-2 text-micro font-medium uppercase text-ink-muted">
                    Vehicle
                  </div>
                  <div className="flex flex-1">
                    {columns.map((column) => (
                      <div
                        key={column.date.toISOString()}
                        aria-current={column.isToday ? 'date' : undefined}
                        className={[
                          'flex-1 border-r border-rule py-1 text-center last:border-r-0',
                          column.isToday
                            ? 'bg-signal text-ink-inverse'
                            : column.isWeekend
                              ? 'bg-paper'
                              : '',
                        ].join(' ')}
                      >
                        {range.showWeekday && (
                          <div
                            className={[
                              'text-micro uppercase',
                              column.isToday
                                ? 'font-semibold text-ink-inverse'
                                : 'text-ink-faint',
                            ].join(' ')}
                          >
                            {column.isToday ? 'Today' : column.weekday}
                          </div>
                        )}
                        {/* In the quarter view a date does not fit in 12px, so
                            only Mondays are numbered — enough to count weeks
                            along, which is what anybody reads a quarter for.
                            The month bands above carry the rest. */}
                        {(range.showDate || column.date.getDay() === 1) && (
                          <div
                            className={[
                              'tnum text-small',
                              column.isToday ? 'font-semibold' : 'font-medium',
                              range.showDate ? '' : 'text-micro text-ink-faint',
                            ].join(' ')}
                          >
                            {column.label}
                          </div>
                        )}
                        {!range.showDate && column.isToday && (
                          <div className="text-micro font-semibold text-ink-inverse">•</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* One row per vehicle */}
              {data?.vehicles.map((vehicle) => {
                const bars = (data.bars ?? []).filter((bar) => bar.vehicle === vehicle.id);
                return (
                  <div key={vehicle.id} className="flex border-b border-rule last:border-b-0">
                    {/* Sticky, and opaque: a transparent sticky cell lets the
                        bars slide underneath the plate they belong to. */}
                    {/* The name leads, the plate sits under it. Staff
                        recognise a car as "the white Hiace", not as
                        BA 2 CHA 1234 — the plate is what gets read out on the
                        phone, which is a different job and a smaller one. */}
                    <div className="sticky left-0 z-10 w-40 shrink-0 border-r border-rule bg-panel px-3 py-2">
                      <div className="truncate text-small font-medium">{vehicle.name}</div>
                      <div className="identifier truncate text-micro text-ink-muted">
                        {vehicle.plate}
                      </div>
                    </div>

                    <div className="relative flex-1">
                      {/* Day grid behind the bars */}
                      <div className="absolute inset-0 flex">
                        {columns.map((column) => (
                          <div
                            key={column.date.toISOString()}
                            className={[
                              'flex-1 border-r border-rule last:border-r-0',
                              // Today gets a tinted stripe the whole height of
                              // the grid, so it is findable without scrolling
                              // back up to the header.
                              column.isToday
                                ? 'bg-signal-wash'
                                : column.isWeekend
                                  ? 'bg-paper'
                                  : '',
                            ].join(' ')}
                          />
                        ))}
                      </div>

                      <div className="relative h-11 py-1.5">
                        {bars.map((bar) => {
                          const position = place(bar.start_at, bar.end_at);
                          const isBlock = bar.kind === 'block';
                          return (
                            <Link
                              key={bar.id}
                              to={isBlock ? '/fleet' : `/bookings/${bar.id}`}
                              title={`${bar.reference} ${bar.title}`}
                              style={position}
                              className={[
                                'absolute top-1.5 flex h-8 items-center overflow-hidden rounded-sm px-1.5 text-micro font-medium',
                                BAR_STYLES[appearanceOf(bar)],
                              ].join(' ')}
                            >
                              <span className="truncate">{bar.title}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          )}
          </>
        )}
      </Panel>

      {/* Colour is never the only channel, so the key spells it out — and it
          names everything the grid can draw, which is the point. A style with
          no entry here is a style nobody can read.

          Hidden in the year view, which draws no bars at all and carries its
          own scale: two keys under one picture is two things to learn. */}
      <div
        className="flex flex-wrap gap-4 px-1 text-small text-ink-muted"
        hidden={range.asTimeline}
      >
        {BAR_LEGEND.map((entry) => (
          <span key={entry.look} className="flex items-center gap-2">
            <span className={`h-3 w-6 rounded-sm ${BAR_STYLES[entry.look]}`} />
            {entry.label}
          </span>
        ))}
      </div>
    </div>
  );
}
